<template>
  <div class="home-item">
    <APageHead
      back
      :breadcrumbs="['시스템', '앱 · 홈 진열 관리', headTitle]"
      :title="headTitle"
      :sub="headSub"
      @back="goList"
    >
      <template #actions>
        <ABtn kind="ghost" icon="edit" :disabled="!section" @click="openEdit">섹션 정보 수정</ABtn>
        <ABtn kind="ghost" icon="smartphone" :disabled="!section" @click="showPreview = true">
          앱 노출 미리보기
        </ABtn>
        <ABtn kind="primary" icon="add" :disabled="!section" @click="openPicker">도서 추가</ABtn>
      </template>
    </APageHead>

    <div v-if="loading" class="home-item__loading">
      <div class="p-spinner p-spinner--lg"></div>
      <p>섹션 정보를 불러오는 중...</p>
    </div>

    <template v-else-if="section">
      <!-- 노출 개수보다 많이 담으면 뒤쪽은 앱에서 안 보입니다. 화면에서 바로 알 수 있게 알립니다. -->
      <div v-if="overflowCount > 0" class="home-item__banner">
        <i class="material-icons-round">visibility_off</i>
        <div>
          <b>뒤쪽 {{ overflowCount }}권은 앱 홈에 나오지 않습니다.</b>
          <span>
            이 섹션의 노출 개수는 <b>{{ section.display_limit }}권</b>입니다.
            더 보여주려면 섹션 정보에서 노출 개수를 늘리거나 순서를 올리세요.
          </span>
        </div>
      </div>

      <div class="home-item__stats">
        <ACard><AStat label="진열 도서" :value="items.length" unit="권" /></ACard>
        <ACard>
          <AStat label="앱 노출 개수" :value="section.display_limit || items.length" unit="권" />
        </ACard>
        <ACard><AStat label="미공개 교재" :value="unpublishedCount" unit="권" /></ACard>
        <ACard>
          <AStat label="섹션 노출" :value="section.active_yn === 'Y' ? '켜짐' : '꺼짐'" />
        </ACard>
      </div>

      <ACard>
        <template #title>
          <span class="home-item__sec-title">
            <i class="material-icons-round home-item__sec-icon">library_books</i>
            진열 도서
          </span>
        </template>
        <template #sub>
          위에 있는 도서가 앱 홈에서 왼쪽(먼저)에 놓입니다.
        </template>
        <template #action>
          <template v-if="orderDirty">
            <APill kind="warn" dot>순서 변경됨</APill>
            <ABtn size="sm" kind="ghost" icon="undo" @click="resetOrder">되돌리기</ABtn>
            <ABtn size="sm" kind="primary" icon="save" :loading="savingOrder" @click="saveOrder">
              순서 저장
            </ABtn>
          </template>
          <APill v-else kind="info">{{ items.length }}권</APill>
        </template>

        <AEmpty
          v-if="!items.length"
          title="진열된 도서가 없습니다"
          sub="'도서 추가'로 이 섹션에 노출할 교재를 골라 담으세요."
        >
          <template #action>
            <ABtn kind="primary" icon="add" @click="openPicker">도서 추가</ABtn>
          </template>
        </AEmpty>

        <ATable v-else :columns="columns" :rows="items" row-key="item_idx" striped bordered>
          <template #cell:_order="{ index }">
            <div class="home-item__order">
              <span :class="['home-item__order-no', { 'is-off': isOverflow(index) }]">
                {{ index + 1 }}
              </span>
              <div class="home-item__order-btns">
                <ABtn
                  size="sm"
                  kind="ghost"
                  icon="arrow_upward"
                  icon-only
                  title="앞으로"
                  :disabled="index === 0"
                  @click="move(index, -1)"
                />
                <ABtn
                  size="sm"
                  kind="ghost"
                  icon="arrow_downward"
                  icon-only
                  title="뒤로"
                  :disabled="index === items.length - 1"
                  @click="move(index, 1)"
                />
              </div>
            </div>
          </template>

          <template #cell:book="{ row, index }">
            <div :class="['home-item__book', { 'is-off': isOverflow(index) }]">
              <img
                class="home-item__cover"
                :src="row.cover_image_url || COVER_FALLBACK"
                :alt="row.title"
                @error="onCoverError"
              />
              <div class="home-item__book-text">
                <span class="home-item__book-title">{{ row.title }}</span>
                <span class="home-item__book-meta">
                  {{ row.publisher || '출판사 미등록' }}
                  <template v-if="row.editor_name"> · {{ row.editor_name }}</template>
                </span>
              </div>
            </div>
          </template>

          <template #cell:classify="{ row }">
            <span class="home-item__classify">
              <template v-if="row.series_name">
                {{ row.series_name }}<template v-if="row.series_volume"> {{ row.series_volume }}권</template>
              </template>
              <template v-else>—</template>
              <template v-if="row.grade_label"> · {{ row.grade_label }}</template>
            </span>
          </template>

          <template #cell:status="{ row }">
            <APill v-if="isItemPublic(row)" kind="ok">앱 공개</APill>
            <APill v-else kind="err" dot>{{ textbookStatusLabel(row.status) }}</APill>
          </template>

          <template #cell:price="{ row }">
            <span class="home-item__price">{{ fmtPrice(row.list_price, row.currency_code) }}</span>
          </template>

          <template #cell:_actions="{ row }">
            <ABtn
              size="sm"
              kind="danger"
              icon="remove_circle_outline"
              icon-only
              title="진열에서 빼기"
              @click="removeItem(row)"
            />
          </template>
        </ATable>

        <template #foot>
          <span class="home-item__foot-hint">
            <i class="material-icons-round">info</i>
            가격은 앱에서 스토어 상품가로 표시됩니다. 여기 금액은 참고용입니다.
          </span>
        </template>
      </ACard>
    </template>

    <AEmpty
      v-else
      title="섹션을 찾을 수 없습니다"
      sub="삭제되었거나 주소가 잘못되었습니다."
    >
      <template #action>
        <ABtn kind="primary" icon="arrow_back" @click="goList">목록으로</ABtn>
      </template>
    </AEmpty>

    <HomeSectionBookPickerModal
      v-model="showPicker"
      :section-idx="Number(sectionIdx)"
      :existing-barcodes="existingBarcodes"
      @added="onItemsAdded"
    />

    <HomeSectionPreviewModal v-model="showPreview" :section="section" :items="items" />

    <!-- 수정 모드라 섹션 키 중복 검사(sections)는 쓰지 않습니다. -->
    <HomeSectionFormModal v-model="showForm" :section="section" @saved="fetchSection" />
  </div>
</template>

<script>
import swal from "sweetalert2";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import HomeSectionBookPickerModal from "./components/HomeSectionBookPickerModal.vue";
import HomeSectionPreviewModal from "./components/HomeSectionPreviewModal.vue";
import HomeSectionFormModal from "./components/HomeSectionFormModal.vue";
import { apiErrorMessage, apiErrorStatus } from "@/utils/apiError";
import {
  pickTitle,
  displayTypeLabel,
  normalizeSection,
  isItemPublic,
  textbookStatusLabel,
  fmtPrice,
  onCoverError,
  COVER_FALLBACK,
  moveItem,
  orderPayload,
  sameOrder,
} from "@/utils/homeSection";

export default {
  name: "SystemHomeSectionDetail",
  components: {
    APageHead,
    ACard,
    AStat,
    ABtn,
    APill,
    ATable,
    AEmpty,
    HomeSectionBookPickerModal,
    HomeSectionPreviewModal,
    HomeSectionFormModal,
  },
  async beforeRouteLeave() {
    if (!this.orderDirty) return true;
    const ok = await swal.fire({
      icon: "warning",
      title: "저장하지 않은 순서가 있습니다",
      text: "이 화면을 벗어나면 바꾼 진열 순서가 사라집니다.",
      showCancelButton: true,
      confirmButtonText: "저장하지 않고 나가기",
      cancelButtonText: "여기 남기",
    });
    return ok.isConfirmed;
  },
  props: {
    // 라우트 param. 문자열로 들어옵니다.
    sectionIdx: { type: [String, Number], required: true },
  },
  data() {
    return {
      loading: true,
      savingOrder: false,
      section: null,
      // items = 화면이 만지는 작업본, serverItems = 마지막으로 서버와 맞춘 스냅샷
      items: [],
      serverItems: [],
      showPicker: false,
      showPreview: false,
      showForm: false,
      COVER_FALLBACK,
      columns: [
        { key: "_order", label: "순서", width: 110, align: "center" },
        { key: "book", label: "도서", width: 320 },
        { key: "barcode", label: "바코드 (ISBN)", width: 150, mono: true },
        { key: "classify", label: "시리즈 · 단계", width: 190, wrap: true },
        { key: "status", label: "교재 상태", width: 120, align: "center" },
        { key: "price", label: "가격", width: 110, align: "right" },
        { key: "_actions", label: "관리", width: 80, align: "right" },
      ],
    };
  },
  computed: {
    headTitle() {
      return this.section ? pickTitle(this.section) : "홈 섹션";
    },
    headSub() {
      if (!this.section) return "";
      const limit = this.section.display_limit
        ? `${this.section.display_limit}권 노출`
        : "진열 전체 노출";
      const active = this.section.active_yn === "Y" ? "앱 노출 중" : "미노출";
      return `${this.section.section_key} · ${displayTypeLabel(this.section.display_type)} · ${limit} · ${active}`;
    },
    existingBarcodes() {
      return this.items.map((i) => i.barcode);
    },
    unpublishedCount() {
      return this.items.filter((i) => !isItemPublic(i)).length;
    },
    // 노출 개수를 넘어서 앱에 안 나오는 권수. display_limit=0 이면 전부 노출이라 0.
    overflowCount() {
      const limit = this.section?.display_limit || 0;
      if (!limit) return 0;
      return Math.max(0, this.items.length - limit);
    },
    orderDirty() {
      return !sameOrder(this.items, this.serverItems, "item_idx");
    },
  },
  created() {
    this.fetchSection();
  },
  methods: {
    isItemPublic,
    textbookStatusLabel,
    fmtPrice,
    onCoverError,
    isOverflow(index) {
      const limit = this.section?.display_limit || 0;
      return !!limit && index >= limit;
    },
    goList() {
      this.$router.push({ name: "SystemHomeSections" });
    },
    /**
     * 순서를 바꿔 둔 채로 다른 작업을 하면, 그 작업의 재조회에 묻혀 순서가 조용히 사라집니다.
     * 재조회를 부르는 모든 동작 앞에 세워 둡니다.
     * @returns {Promise<boolean>} 계속 진행해도 되면 true
     */
    async ensureOrderSaved() {
      if (!this.orderDirty) return true;
      const ok = await swal.fire({
        icon: "warning",
        title: "저장하지 않은 순서가 있습니다",
        text: "먼저 진열 순서를 저장한 뒤 이어서 진행합니다. 순서를 버리려면 '되돌리기'를 누르세요.",
        showCancelButton: true,
        confirmButtonText: "순서 저장 후 계속",
        cancelButtonText: "취소",
      });
      if (!ok.isConfirmed) return false;
      return this.saveOrder();
    },
    async openEdit() {
      if (!(await this.ensureOrderSaved())) return;
      this.showForm = true;
    },
    async openPicker() {
      if (!(await this.ensureOrderSaved())) return;
      this.showPicker = true;
    },
    async fetchSection() {
      this.loading = true;
      try {
        const res = await this.$axios({
          method: "get",
          url: `/emp/home/sections/${this.sectionIdx}`,
          loadingBar: true,
        });
        const data = res.data || {};
        this.section = normalizeSection(data);
        const items = [...(data.items || [])];
        items.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        this.items = items;
        this.serverItems = [...items];
      } catch (e) {
        this.section = null;
        this.items = [];
        this.serverItems = [];

        // 404 는 "없는 섹션"이거나 "아직 배포되지 않은 API"다. 둘 다 아래 빈 상태가 답이므로
        // 오류 모달까지 띄우지 않는다. 나머지(401·500 등)는 원인을 알려야 하므로 모달을 띄운다.
        if (apiErrorStatus(e) === 404) {
          console.info("홈 섹션 상세 404:", this.sectionIdx);
        } else {
          console.error("홈 섹션 상세 조회 실패:", e);
          swal.fire("오류", apiErrorMessage(e, "섹션 정보를 불러오지 못했습니다."), "error");
        }
      } finally {
        this.loading = false;
      }
    },
    move(index, dir) {
      this.items = moveItem(this.items, index, dir);
    },
    resetOrder() {
      this.items = [...this.serverItems];
    },
    /** @returns {Promise<boolean>} 저장에 성공했으면 true */
    async saveOrder() {
      this.savingOrder = true;
      try {
        const res = await this.$axios({
          method: "put",
          url: `/emp/home/sections/${this.sectionIdx}/items/order`,
          data: { orders: orderPayload(this.items, "item_idx") },
          loadingBar: true,
        });
        this.$notifyVue({ type: "success", title: "진열 순서를 저장했습니다." });
        const items = res.data?.items;
        if (Array.isArray(items) && items.length) {
          const next = [...items].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
          this.items = next;
          this.serverItems = [...next];
        } else {
          await this.fetchSection();
        }
        return true;
      } catch (e) {
        console.error("진열 순서 저장 실패:", e);
        swal.fire("저장 실패", apiErrorMessage(e, "진열 순서를 저장하지 못했습니다."), "error");
        await this.fetchSection();
        return false;
      } finally {
        this.savingOrder = false;
      }
    },
    onItemsAdded() {
      this.fetchSection();
    },
    async removeItem(row) {
      if (!(await this.ensureOrderSaved())) return;
      const ok = await swal.fire({
        icon: "warning",
        title: "진열에서 뺍니다",
        text: `'${row.title}' 을 이 섹션에서 내립니다. 교재 자체는 삭제되지 않습니다.`,
        showCancelButton: true,
        confirmButtonText: "빼기",
        cancelButtonText: "취소",
      });
      if (!ok.isConfirmed) return;

      try {
        await this.$axios({
          method: "delete",
          url: `/emp/home/sections/${this.sectionIdx}/items/${row.item_idx}`,
          loadingBar: true,
        });
        this.$notifyVue({ type: "success", title: `'${row.title}' 을 진열에서 뺐습니다.` });
        this.fetchSection();
      } catch (e) {
        console.error("진열 도서 삭제 실패:", e);
        swal.fire("삭제 실패", apiErrorMessage(e, "도서를 빼지 못했습니다."), "error");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.home-item {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 64px 24px;
    color: $p-ink-mid;

    p { margin: 0; font-size: 12px; }
  }

  &__banner {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 13px 16px;
    background: $p-warn-soft;
    border: 1px solid rgba($p-warn, 0.35);
    border-radius: $p-radius-md;

    > i {
      font-size: 20px !important;
      color: $p-warn-ink;
    }

    > div {
      display: flex;
      flex-direction: column;
      gap: 2px;

      b { font-size: 12px; font-weight: 800; color: $p-ink; }
      span { font-size: 11px; line-height: 1.5; color: $p-ink-mid; }
    }
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;

    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__sec-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
  }

  &__sec-icon {
    color: $p-point-deep;
    font-size: 16px !important;
  }

  &__order {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  &__order-no {
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 700;
    color: $p-ink-soft;
    min-width: 14px;

    &.is-off { color: $p-ink-softer; text-decoration: line-through; }
  }

  &__order-btns {
    display: inline-flex;
    gap: 2px;
  }

  &__book {
    display: flex;
    align-items: center;
    gap: 10px;

    // 노출 개수를 넘어선 행은 흐리게 — 앱에서 안 보이는 상태를 그대로 표현합니다.
    &.is-off { opacity: 0.45; }
  }

  &__cover {
    width: 34px;
    height: 48px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid $p-line;
    background: $p-panel-alt;
    flex-shrink: 0;
  }

  &__book-text {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  &__book-title {
    font-size: 12px;
    font-weight: 800;
    color: $p-ink;
    white-space: normal;
    line-height: 1.35;
  }

  &__book-meta {
    font-size: 10px;
    color: $p-ink-soft;
  }

  &__classify,
  &__price {
    font-size: 12px;
    color: $p-ink-mid;
  }

  &__price {
    font-family: $p-font-mono;
    font-weight: 700;
    color: $p-ink;
  }

  &__foot-hint {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-right: auto;
    font-size: 11px;
    color: $p-ink-soft;

    > i { font-size: 14px !important; }
  }
}
</style>
